
document.addEventListener('DOMContentLoaded', async function () {
    const API_BASE_URL = 'https://app.alyvewellness.com/api';
    //const API_BASE_URL = 'http://localhost:8000/api';
     const POPULATED_URL = `${API_BASE_URL}/proxy_get_populated_questionnaire_result`;
     const RECOVERY_URL = `${API_BASE_URL}/proxy_recover_quiz_by_token`;

     // ── Quiz recovery token kept in the Shopify cart ─────────────────────────
     // Safari wipes localStorage after 7 days without a visit, but the cart cookie
     // is set by Shopify's own servers and survives. The backbone returns a
     // cart-scoped recovery token (`use: 'cart'`) with a guest's populated result;
     // it is kept as a cart attribute and redeemed like the emailed link when
     // localStorage is empty. Only cart-scoped tokens are ever written: cart
     // attributes are copied onto the order, and the backbone refuses a cart token
     // once the quiz belongs to a customer, so a copy on an order opens nothing.
     const CART_QUIZ_TOKEN_KEY = '_alyve_quiz_token';

     const isLoggedInCustomer = () =>
       !!window.customer_id && !String(window.customer_id).includes('guest');

     function isCartScopedToken(token) {
       try {
         const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
         return JSON.parse(atob(payload)).use === 'cart';
       } catch (e) {
         return false;
       }
     }

     async function readCartQuizToken() {
       try {
         const response = await fetch('/cart.js');
         if (!response.ok) return null;
         const cart = await response.json();
         return (cart && cart.attributes && cart.attributes[CART_QUIZ_TOKEN_KEY]) || null;
       } catch (e) {
         return null;
       }
     }

     // Removing it goes through window.alyveClearCartQuizToken (theme.liquid), the
     // one place restart and logout use too.
     function writeCartQuizToken(token) {
       return fetch('/cart/update.js', {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ attributes: { [CART_QUIZ_TOKEN_KEY]: token } }),
       }).catch((e) => console.warn('Could not update the cart quiz token:', e));
     }

     // Set when this page restored the quiz from the cart: the token was just read
     // from the cart, so there is nothing to sync back.
     let restoredFromCart = false;

     // Runs in the background once results are on screen. The token is the same
     // string on every request for a quiz, so most visits find it already there
     // and write nothing.
     //
     // Other scripts write to the cart as the page loads: Convert and Intelligems
     // set their own attributes. When two cart updates land together Shopify keeps
     // only the last, so a write made alongside theirs can be lost. So wait for them
     // to settle, then confirm the token is in the cart and write it again if not,
     // a few times at most.
     const CART_SYNC_DELAYS_MS = [1500, 2000, 2000];

     // window.alyveCartBusy is the one lock on cart writes, shared with add to cart in
     // the results section: a token write landing at the same moment as the add can
     // make Shopify drop the added plan. The token write waits for it and holds it
     // while writing, and add to cart waits for the token write in turn.
     async function waitForCartIdle(maxMs = 10000) {
       const start = Date.now();
       while (window.alyveCartBusy && Date.now() - start < maxMs) {
         await new Promise((resolve) => setTimeout(resolve, 250));
       }
     }

     async function syncCartQuizToken(populated) {
       const token = populated && populated.recovery_token;
       if (!token || !isCartScopedToken(token) || isLoggedInCustomer()) return;
       if (restoredFromCart) return;
       for (const delay of CART_SYNC_DELAYS_MS) {
         await new Promise((resolve) => setTimeout(resolve, delay));
         await waitForCartIdle();
         if ((await readCartQuizToken()) === token) return;
         await waitForCartIdle();
         // Checked and taken in the same tick, so add to cart cannot start in between.
         // Still busy after the wait means a long add: try again on the next pass.
         if (window.alyveCartBusy) continue;
         window.alyveCartBusy = true;
         try {
           await writeCartQuizToken(token);
         } finally {
           window.alyveCartBusy = false;
         }
       }
     }

     // ── Quiz recovery via emailed token, or the token kept in the cart ───────
     // If the page is opened from a recovery link (?quiz_token=...), or a guest's
     // localStorage has been wiped but their cart still holds a recovery token,
     // fetch the quiz data from the middleware and write it into the SAME
     // localStorage keys the page already uses, so the rest of the flow renders it
     // exactly like a locally-generated quiz.
     async function recoverQuizFromToken() {
       let quizToken = null;
       try {
         quizToken = new URLSearchParams(window.location.search).get('quiz_token');
       } catch (e) {
         return false;
       }

       // Logged-in customers already get their latest quiz via the authenticated
       // API in the normal flow, so skip the token path for them. This avoids
       // overwriting a logged-in customer's localStorage with the quiz the link
       // was generated for (which may be older than their current one).
       if (isLoggedInCustomer()) {
         return false;
       }

       // No emailed link: fall back to the cart, but only when this browser has no
       // quiz of its own, so localStorage always wins and a normal visit costs
       // nothing. theme.liquid renders window.alyveCartHasQuizToken from the cart,
       // which saves the /cart.js request for visitors who have no token.
       let fromCart = false;
       if (!quizToken) {
         if (localStorage.getItem('alyve.current_consultation')) return false;
         if (window.alyveCartHasQuizToken !== true) return false;
         quizToken = await readCartQuizToken();
         if (!quizToken || !isCartScopedToken(quizToken)) return false;
         fromCart = true;
       }

       // A cart token the backbone refused is removed, so the quiz buttons stop
       // offering results that cannot load. Only on its refusal, which recover
       // answers with reason 'not_found' (bad or expired token, unknown quiz, or a
       // cart token whose quiz now belongs to a customer). A server error, a 502
       // from the middleware or a network failure may be temporary, and the token
       // is this guest's only way back, so it stays for the next visit.
       const dropRefusedCartToken = (body) => {
         if (fromCart && body && body.reason === 'not_found' && window.alyveClearCartQuizToken) {
           window.alyveClearCartQuizToken();
         }
       };

       const controller = new AbortController();
       const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s safety timeout (covers the full request incl. body read)
  
       try {
         const response = await fetch(
           `${RECOVERY_URL}?quiz_token=${encodeURIComponent(quizToken)}`,
           { method: 'GET', signal: controller.signal }
         );
  
         if (!response.ok) {
           console.warn('Quiz recovery failed, HTTP status:', response.status);
           dropRefusedCartToken(await response.json().catch(() => null));
           return false;
         }

         const data = await response.json();

         if (!data || data.success !== true || !data.populated) {
           console.warn('Quiz recovery returned no usable data:', data);
           dropRefusedCartToken(data);
           return false;
         }

         // Store the populated result exactly where the page already looks for it.
         // recover does not return a token, so a cart token is kept with the result:
         // syncCartQuizToken then finds it, and puts it back if the cart is replaced.
         const populated = fromCart
           ? { ...data.populated, recovery_token: quizToken }
           : data.populated;
         localStorage.setItem('alyve_populated_quiz', JSON.stringify(populated));
         if (fromCart) restoredFromCart = true;
  
         // Build / merge the consultation record so backboneId resolves downstream
         let consultation = {};
         try {
           consultation = JSON.parse(localStorage.getItem('alyve.current_consultation')) || {};
         } catch (e) {
           consultation = {};
         }
         consultation.backboneId = data.quiz_id;
         consultation.quizId = data.quiz_id;
         consultation.completed = true;
         if (data.quiz_summary && data.quiz_summary.first_name) {
           consultation.first_name = data.quiz_summary.first_name;
           // Persist the name separately so customerData.firstName can pick it up
           // on a fresh device where there's no logged-in customer / customer_data.
           localStorage.setItem('alyve_recovered_first_name', data.quiz_summary.first_name);
         }
  
         // Map the raw questionnaire answers into the consultation record so the
         // sections that render directly from the quiz answers work on a fresh
         // device: profile (age/gender), "You told us", and wellness scores.
         // Those read currentQuestionnaire.questions and/or .results.questions,
         // so we populate both access paths.
         const recoveredResults =
           data.results || (Array.isArray(data.questions) ? { questions: data.questions } : null);
         if (recoveredResults) {
           consultation.results = recoveredResults;
           if (Array.isArray(recoveredResults.questions)) {
             consultation.questions = recoveredResults.questions;
           }
         }
  
         localStorage.setItem('alyve.current_consultation', JSON.stringify(consultation));
  
         // Remove quiz_token from the URL so it isn't re-used on refresh / if shared
         if (!fromCart) {
           try {
             window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
           } catch (e) {
             console.warn('Could not strip quiz_token from URL:', e);
           }
         }
  
         return true;
       } catch (error) {
         console.warn('Quiz recovery request errored / timed out:', error);
         return false;
       } finally {
         // Clear the abort timer only after the whole request — including reading
         // the response body — completes. This way the 15s timeout also covers a
         // stalled body download (weak-signal "lie-fi"), not just the headers.
         clearTimeout(timeoutId);
       }
     }
  
     // Loader + safety-net timer are set up FIRST — before any awaited work —
     // so the force-remove timer is always armed even if recovery stalls on a
     // weak connection. (Previously this lived after `await recoverQuizFromToken()`,
     // so a stalled recovery meant the safety net was never even scheduled.)
     const loader = document.getElementById("page-loader");
     const loaderImage = document.getElementById("loader-image");
     const loaderSpinner = document.getElementsByClassName("loader-spinner");
     const ANIMATION_DURATION = 100; // 10 seconds for the animation to complete
  
     // Track animation and data loading state
     let animationComplete = true;
     let dataLoaded = false;
  
     // Function to check if both conditions are met and remove loader
     function checkAndRemoveLoader() {
       if (animationComplete && dataLoaded && loader) {
         loader.classList.add("fade-out");
         setTimeout(() => loader?.remove(), 300);
       }
     }
  
     // The buy button stays disabled until the formula has loaded. When the loader
     // has to come down without it (slow or failed data), say so beside the button
     // rather than leave a dead button with no explanation. The results section
     // removes this the moment it enables the button.
     function showFormulaNotice(kind) {
       if (!document.querySelector('[data-awaiting-formula]')) return;
       const anchor = document.querySelector('#al-buybox .al-cta') ||
         document.querySelector('[data-awaiting-formula]');
       if (!anchor || !anchor.parentNode) return;
       let notice = document.getElementById('alyve-formula-notice');
       if (!notice) {
         notice = document.createElement('div');
         notice.id = 'alyve-formula-notice';
         notice.setAttribute('role', 'status');
         notice.style.cssText = 'margin:12px 0 0;padding:12px 14px;border-radius:10px;background:#fff7e6;color:#5c4400;font-size:14px;line-height:1.45;text-align:center;';
         anchor.parentNode.insertBefore(notice, anchor.nextSibling);
       }
       notice.textContent = kind === 'error'
         ? "We couldn't load your personalised formula. "
         : 'Your personalised formula is taking longer than usual to load. ';
       const retry = document.createElement('a');
       retry.href = '#';
       retry.textContent = 'Try again';
       retry.style.cssText = 'color:inherit;font-weight:600;text-decoration:underline;';
       retry.addEventListener('click', (e) => {
         e.preventDefault();
         window.location.reload();
       });
       notice.appendChild(retry);
     }

     window.alyveShowFormulaNotice = showFormulaNotice;
     window.alyveHideFormulaNotice = function () {
       const notice = document.getElementById('alyve-formula-notice');
       if (notice) notice.remove();
     };

     // Maximum timeout to remove loader (safety net). The results are behind the
     // loader until they arrive, so this is also when the late-formula notice shows:
     // early enough that the customer is still here. The fetch keeps retrying, and
     // the results section enables the button and removes the notice when it lands.
     const MAX_LOAD_TIME = 10000; // 10 seconds max
     setTimeout(() => {
       if (!dataLoaded) {
         console.warn('⚠️ Maximum load time exceeded, removing loader...');
         dataLoaded = true;
         checkAndRemoveLoader();
         showFormulaNotice('slow');
       }
     }, MAX_LOAD_TIME);
  
     // Run recovery BEFORE reading localStorage
     await recoverQuizFromToken();
  
     // If a logged-in customer is viewing data that was left behind by a token
     // recovery (guest flow), purge it so the authenticated flow loads THEIR own
     // latest quiz instead of the stale recovered one. The recovered-first-name
     // key is set only by recoverQuizFromToken, so it reliably marks recovery data.
     if (
       window.customer_id &&
       !String(window.customer_id).includes('guest') &&
       localStorage.getItem('alyve_recovered_first_name')
     ) {
       localStorage.removeItem('alyve_populated_quiz');
       localStorage.removeItem('alyve.current_consultation');
       localStorage.removeItem('alyve_recovered_first_name');
     }
  
     let availableQuestionnaires = localStorage.getItem('alyve.current_consultation');
     if(availableQuestionnaires){
     }else{
     }
    
         let id;
         let name;
         let email;
         let signature;
         let customerData;
         let allProducts;
         let productsByCategory;
         let recommendedProducts;
         let blogs;
         let finalBackboneId;
         let currentQuestionnaire;
         let firstQuiz;
        
        // Initialize global flag for subscription check (default to false)
        window.hasPersonalisedVitaminsSubscription = false;
   
        
        
        const alyveDataSignature = localStorage.getItem("alyve-data-signature") || null;
         const guestId = localStorage.getItem("alyve-guest-id") || null;
         const customerDataFromLocalStorage = localStorage.getItem("alyve_customer_data") || null;
         let local_store_quiz_data = null;
         if (availableQuestionnaires) {
           try {
             local_store_quiz_data = JSON.parse(availableQuestionnaires);
           } catch (e) {
             console.warn('Malformed quiz data in localStorage, clearing and fetching from API:', e);
             localStorage.removeItem('alyve.current_consultation');
             availableQuestionnaires = null;
           }
         }
  
         
    
    
         let cameFromQuiz;
         const storedcameFromQuiz = sessionStorage.getItem('cameFromQuiz');
    
         if (storedcameFromQuiz === 'true') {
           cameFromQuiz = false;
           sessionStorage.removeItem('cameFromQuiz');
         } else {
           cameFromQuiz = false
         }
    
         
        // if (loader && loaderImage) {
        //   loaderImage.style.display = "block";
        //   loaderImage.style.opacity = "0";
        //   console.log("cameFromQuiz" , cameFromQuiz);
        //   if (cameFromQuiz) {
        //     loader.style.background = "#fff";
        //     loaderImage.src = "https://cdn.shopify.com/s/files/1/0375/9158/0731/files/ProgressBar_alyve-loader-pills.gif?v=1745849421";
            
        //     loader.classList.add("show_loader");
        //     loaderImage.style.animation = "loaderFadeIn 0.4s ease-in-out";
        //     loaderImage.style.width = "50%";
        //     loaderImage.style.height = "auto";
        //     //loaderImage.style.maxWidth = "400px"; // Optional: set a max width
        //     loaderImage.style.margin = "0 auto"; // Center the image
              
        //     // Start animation timer for pill gif (only for cameFromQuiz)
        //     setTimeout(() => {
        //       animationComplete = true;
        //       checkAndRemoveLoader();
        //     }, ANIMATION_DURATION);
        //   } else {
        //     loaderImage.style.opacity = "1";
        //     loader.style.background = "white";
        //     loaderImage.src = "https://cdn.shopify.com/s/files/1/0375/9158/0731/files/ALYVE_New_Loader.gif?v=1764448029";
        //     // For non-quiz loads, mark animation as complete immediately
        //     animationComplete = true;
        //   }
        
        // }
    
    
       if (!id && guestId) id = guestId;
       if (!signature && alyveDataSignature) signature = alyveDataSignature;
    
       // A live Shopify session outranks the cached identity. alyve_customer_data is written by
       // four dashboard scripts and cleared only on logout, so on a shared browser it can still
       // hold the previous customer and would otherwise decide who this page fetches for.
       //
       // Only applied when a real customer is logged in. For guests window.customer_id is a fresh
       // guest uuid on every page load, so it is not an identity worth comparing against and using
       // it here would wipe a guest's in-progress quiz on every visit.
       const liveCustomerId =
         window.customer_id && !String(window.customer_id).includes("guest")
           ? String(window.customer_id)
           : null;

       if (customerDataFromLocalStorage) {
         let parsed;
         try {
           parsed = JSON.parse(customerDataFromLocalStorage);
         } catch (e) {
           // Matches how alyve.current_consultation is handled above. Without this a corrupted
           // entry throws inside DOMContentLoaded and aborts the identity resolution below it,
           // leaving the customer behind the loader until the safety-net timeout.
           console.warn("Malformed alyve_customer_data, clearing and using the live session:", e);
           localStorage.removeItem("alyve_customer_data");
           parsed = {};
         }

         const cachedIdIsRealCustomer = !!parsed.id && !String(parsed.id).includes("guest");

         const cachedIsAnotherCustomer =
           // A live customer outranks ANY mismatched cached id, real or guest-flavoured -
           // a stale guest's cached data is just as much someone else's identity as a
           // different real customer's would be.
           (liveCustomerId && parsed.id && String(parsed.id) !== liveCustomerId) ||
           // No live session right now (native /account/logout, session expiry, closed tab -
           // anything other than the in-app Logout link), but the cache still claims a real
           // customer. There is nothing to compare it against any more, so it cannot be trusted
           // as this visitor's identity - AUD-004. A true guest's own cached guest id is left
           // alone so their in-progress quiz still survives a reload.
           (!liveCustomerId && cachedIdIsRealCustomer);

         if (cachedIsAnotherCustomer) {
           // Discard rather than merge, so nothing downstream inherits the previous customer's
           // id, signature, quiz reference or cached results.
           console.warn("Cached identity belongs to a different customer, discarding");
           localStorage.removeItem("alyve_customer_data");
           localStorage.removeItem("alyve.current_consultation");
           localStorage.removeItem("alyve-data-signature");
           localStorage.removeItem("alyve_populated_quiz");
           localStorage.removeItem("alyve_recovered_first_name");
           availableQuestionnaires = null;
           local_store_quiz_data = null;
           // A live session still outranks any leftover guest id from an earlier
           // anonymous visitor on this device; only adopt guestId when there is no
           // live customer to resolve to instead.
           id = liveCustomerId || guestId || null;
           signature = null;
         } else if (parsed.id) {
           name = parsed.firstName || parsed.first_name || local_store_quiz_data?.first_name || "";
           name = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase(); // cap first letter no matter the input
           email = parsed.email || "";
           if (!signature) signature = parsed.signature;
           id = parsed.id;
         }
       }
    
       // Resolve id the same way customerData.id below does, so stringId/isGuest
       // reflect the value actually used rather than the pre-fallback id, which
       // can be null here even though a live guest window.customer_id exists.
       const resolvedId = id || window.customer_id;
       const stringId = String(resolvedId);

       if(!!stringId && !stringId.includes("guest")) {
         localStorage.removeItem("alyve-guest-id");
       }


      const recoveredFirstName = localStorage.getItem('alyve_recovered_first_name') || "";

      customerData = {
        firstName: name || window.customer_first_name || local_store_quiz_data?.results?.first_name || recoveredFirstName || "",
        email: email || window.customer_email || local_store_quiz_data?.results?.email || "",
        id: resolvedId,
        signature: signature || window.token_hmac,
        isGuest: stringId.includes("guest"),
        hasPersonalisedVitaminsSubscription: false // Will be updated after checking subscriptions
      }
    
    
    
    
    if(customerData.isGuest && window.customer_email){
            localStorage.removeItem('alyve.current_consultation');
            localStorage.removeItem('alyve-guest-id');
            localStorage.removeItem('alyve-data-signature');
            window.location.href = '/pages/quiz-app';
    }
    
    // helper to get latest questionnaire (fetch version)
    async function getLatestQuestionnaire(customerData, limit = 1) {
     try {
       const tokenId = btoa('gid://shopify/Customer/' + customerData.id);
       const response = await fetch(`${API_BASE_URL}/proxy_get_latest_questionnaire`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
            "x-customer-email": window.customer_email,
            "x-customer-id": window.customer_id,
            "x-customer-signature": window.token_hmac,
           'x-shopify-shop-domain': 'alyve-store.myshopify.com',
         },
         body: JSON.stringify({
           tokenId: tokenId,
           limit: 1
         })
       });
    
       if (!response.ok) throw new Error(`HTTP error! ${response.status}`);
       const data = await response.json();
       return data;
     } catch (error) {
       console.error('Failed to load latest questionnaire:', error);
       return null;
     }
    }
    
    
    async function getCustomerById (id) {
     const customerUrl = `${API_BASE_URL}/proxy_get_customer`;
      const payload = {
     customerId: id
    };
     const response = await fetch(customerUrl, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'x-customer-email': customerData.email,
         'x-customer-id': customerData.id,
         'x-customer-signature': customerData.signature,
         'x-shopify-shop-domain': 'alyve-store.myshopify.com',
       },
       body: JSON.stringify(payload),
     });
    
     if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
     const customerResponse = await response.json();
     return customerResponse;
    
    }
    
    // These two used to run back to back, so the page paid for one round trip
    // before starting the next. Neither reads the other's result: getCustomerById
    // works off customerData.id, getActiveSubscriptions off the window globals.
    // allSettled rather than all, so one failing still leaves the other handled
    // with its own fallback, exactly as the separate try/catch blocks did.
    if (!customerData.isGuest) {
      const checkSubscription = Boolean(window.customer_id);

      const [customerResult, subscriptionResult] = await Promise.allSettled([
        getCustomerById(customerData.id),
        checkSubscription ? getActiveSubscriptions() : Promise.resolve(),
      ]);

      if (customerResult.status === 'fulfilled') {
        const { customer } = customerResult.value || {};
        customerData = {
          ...customerData,
          ...customer,
        };
      } else {
        // Continue with existing customerData from localStorage
        console.error('Failed to fetch customer data:', customerResult.reason);
      }

      if (!checkSubscription) {
        customerData.hasPersonalisedVitaminsSubscription = false;
      } else if (subscriptionResult.status === 'rejected') {
        console.error('Failed to check subscriptions, defaulting to false:', subscriptionResult.reason);
        customerData.hasPersonalisedVitaminsSubscription = false;
      }
   }else{
    customerData.hasPersonalisedVitaminsSubscription = false;
  }
  
  
   
   const findProductByVariantId = (products, variantId) => {
    return products.find(product => product.variantId === variantId);
    };
    
    const sortProductsByPriceHighToLow = (products) => {
    return [...products].sort((a, b) => {
     if (a.price > b.price) return -1;
     if (a.price < b.price) return 1;
     return 0;
    });
    };
    
    
    
    
    
    //function to parse product data
    function parseProducts(products, category = undefined) {
    if (!products || !Array.isArray(products)) {
     return [];
    }
    
    if (category == 'personalised_vitamin') {
     products = products.map((product) => {
       const {
         productId,
         variantId,
         isOnSale,
         isSelected,
         isPopular: popular,
         savingsText: saveText,
         subTitle: sub,
         title,
         panelTitle,
         mobilePanelTitle,
         shownPrice: price,
         price: totalPrice,
         priceSub,
         isSubscription: recharge,
         showOnPanel,
         properties,
         sellingPlanId,
         category
       } = product;
    
       return {
         productId,
         variantId,
         isOnSale,
         isSelected,
         popular,
         saveText,
         sub,
         title,
         panelTitle,
         mobilePanelTitle,
         price,
         totalPrice,
         priceSub,
         recharge,
         showOnPanel,
         sellingPlanId,
         properties,
         category
       };
     });
    
     const oneTimeProduct = findProductByVariantId(products, 'Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0VmFyaWFudC8zMzY3ODgzMzAyNTA4Mw==');
     products = sortProductsByPriceHighToLow(
       products.filter(p => p.variantId !== 'Z2lkOi8vc2hvcGlmeS9Qcm9kdWN0VmFyaWFudC8zMzY3ODgzMzAyNTA4Mw==')
     );
     
     if (oneTimeProduct) {
       products.push(oneTimeProduct);
     }
    }
    
    // Filter out products that are not on sale
    products = products.filter(product => product.isOnSale === true);
    
    return products;
    }
    
    
    
    
    
    const apiHeaders = {
         'Content-Type': 'application/json',
         'x-customer-email': customerData.email,
         'x-customer-id': customerData.id,
         'x-customer-signature': customerData.signature,
         'x-shopify-shop-domain': 'alyve-store.myshopify.com',
       }
    
    
    
    
    
    const getProductsByCategory = async (category) => {
    if (!category) {
    console.error('No category provided');
    return [];
    }
    
     const productsByCategoryUrl = `${API_BASE_URL}/proxy_get_products_by_category`;
     const payload = {
       category: category
     }
    try {
     const response = await fetch(productsByCategoryUrl, {
       method: 'POST',
       headers: apiHeaders,
       body: JSON.stringify(payload),
     });
    
     if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
     const productsByCategoryResponse = await response.json();
     return productsByCategoryResponse;
    } catch (error) {
    console.error(`Error fetching products for category ${category}:`, error);
    return []
    }
    };
    
    
    const getProducts = async () => {
     const productsUrl = `${API_BASE_URL}/proxy_get_products`;
     const payload = {}
        try {
        const response = await fetch(productsUrl, {
          method: 'POST',
          headers: apiHeaders,
          body: JSON.stringify(payload),
        });
        
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const productsResponse = await response.json();
        return productsResponse;
        } catch (error) {
        console.error('Error fetching all products', error);
        return []
        }
    };
    
    
    const getRecommendedProducts = async (limit = 7) => {
    
    
    const guestId = localStorage.getItem('alyve-guest-id');
    
    const recommededProductsUrl = `${API_BASE_URL}/proxy_get_recommended_products`;
     const payload = {
       customerToken: customerData?.id ,
       isGuest: String(customerData?.id)?.includes("guest"),
       limit
     }
    
    try {
     const response = await fetch(recommededProductsUrl, {
       method: 'POST',
       headers: apiHeaders,
       body: JSON.stringify(payload),
     });
    
     if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
     const recommededResponse = await response.json();
     return recommededResponse.products;
    } catch (error) {
    console.error('Error fetching recommended products', error);
    return []
    }
    };
    
    const getIngredientBlogs = async () => {
    
    const url = `${API_BASE_URL}/proxy_getIngredientBlogs`;
    
    try {
     const response = await fetch(url, {
       method: 'GET',
       headers: apiHeaders,
     });
    
     if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
         const blogsResponse = await response.json();
         return blogsResponse.data;
         
    } catch (error) {
    console.error('Error fetching blogs', error);
    return []
    }
    };
    
    
    
    
    
    
    const fetchProducts = async (category) => {
       try {
         let productsData;
         
         if (category) {
           productsData = await getProductsByCategory(category);
         } else {
           productsData = await getProducts();
           
         }
         
         // Parse and process products
         const parsedProducts = parseProducts(productsData, category);
         return parsedProducts;
       } catch (err) {
         console.error('Error fetching products:', err);
         
       } 
     };
    
    
    
    async function getFirstQuiz(userId) {
       try {
           const response = await fetch(`${API_BASE_URL}/proxy_get_first_quiz`, {
               method: 'POST',
               headers: {
                 "Content-Type": "application/json",
                 "x-customer-email": window.customer_email,
                 "x-customer-id": window.customer_id,
                 "x-customer-signature": window.token_hmac,
                 "x-shopify-shop-domain": "alyve-store.myshopify.com",
               },
               body: JSON.stringify({
                   userId: userId  // Pass the user ID
               })
           });
   
           const result = await response.json();
           
           if (result.success) {
               return result.data;
           } else {
               console.error('Error:', result.error);
               throw new Error(result.error);
           }
       } catch (error) {
           console.error('Failed to fetch first quiz:', error);
           throw error;
       }
   }
  
  
  async function getActiveSubscriptions(){
     const subPayloadold = {
      token: btoa(`${window.customer_id}`),
      customerToken: btoa(`gid://shopify/Customer/${window.customer_id}`),
    };
  
    try {
      const response = await fetch(`${API_BASE_URL}/proxy_get_recharge_subscription`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "x-customer-email": window.customer_email,
          "x-customer-id": window.customer_id,
          "x-customer-signature": window.token_hmac,
          "x-shopify-shop-domain": "alyve-store.myshopify.com",
        },
        body: JSON.stringify(subPayloadold)
      });
  
      if (!response.ok) {
        console.warn('⚠️ Subscription API returned non-OK status:', response.status);
        // Default to false - safer to show Add to Cart if API fails
        customerData.hasPersonalisedVitaminsSubscription = false;
        return false;
      }
  
      const result = await response.json();
  
      // Extract subscriptions data
      let subscriptions = result.data?.subscriptions_data || [];
  
      // Filter only ACTIVE subscriptions (same logic as home_presonalized.js)
      const activeSubscriptions = subscriptions.filter(
        (sub) => sub.status === "ACTIVE"
      );
  
  
      // Look for 'Personalised Vitamins' in ACTIVE subscriptions
      const personalisedSubscription = activeSubscriptions.find((sub) =>
        sub.product_title.toLowerCase().includes("personalised vitamins")
      );
  
      // Update customerData flag
      customerData.hasPersonalisedVitaminsSubscription = !!personalisedSubscription;
  
  
      return customerData.hasPersonalisedVitaminsSubscription;
  
    } catch (error) {
      console.error('❌ Failed to fetch subscriptions:', error);
      // Default to false on error - safer to show Add to Cart
      customerData.hasPersonalisedVitaminsSubscription = false;
      return false;
    }
  }
   
   
   // // Fetch all products with individual error handling
    try {
      allProducts = [] //await fetchProducts();
    } catch (error) {
      console.error('Failed to fetch all products:', error);
      allProducts = []; // Fallback to empty array
    }
  
    try {
      productsByCategory = [] // await getProductsByCategory('personalised_vitamin');
    } catch (error) {
      console.error('Failed to fetch products by category:', error);
      productsByCategory = []; // Fallback to empty array
    }
  
    // Both requests are started here and awaited where their results are first
    // needed. They do not read each other's response, so waiting for the first
    // before sending the second cost a whole round trip for nothing. Guests skip
    // the quiz call entirely, as they did before.
    const recommendedProductsRequest = getRecommendedProducts();
    const firstQuizRequest = customerData.isGuest
      ? null
      : getFirstQuiz(window.customer_id);

    // getFirstQuiz rethrows, and it is not awaited until after the recommended
    // products call resolves. Attaching a handler now marks the rejection as
    // handled so it cannot surface as a transient unhandledrejection in that
    // gap; the await below still receives it and the try/catch there still runs.
    if (firstQuizRequest) firstQuizRequest.catch(() => {});

    try {
      recommendedProducts = await recommendedProductsRequest;
    } catch (error) {
      console.error('Failed to fetch recommended products:', error);
      recommendedProducts = []; // Fallback to empty array
    }
  
  
  
  
  
  
    
    
    let loggedInGender = '';
    let loggedInAge = '';
  
    if(!customerData.isGuest){
      
      try {
        firstQuiz = await firstQuizRequest;
  
        const {date, results} = firstQuiz;
        loggedInGender = results.find((question)=>question.uniqueId === 40 || question.id === 40 )?.answer || '';
        const age = results.find((question)=>question.uniqueId === 4 || question.id === 4)?.answer;
  
        if (age && date) {
          // Parse the date from the quiz
          const quizDate = new Date(date);
          const currentDate = new Date();
          
          // Calculate years passed using a more accurate method
          let yearsPassed = currentDate.getFullYear() - quizDate.getFullYear();
          
          // Adjust if the current date hasn't reached the anniversary yet this year
          const monthDiff = currentDate.getMonth() - quizDate.getMonth();
          const dayDiff = currentDate.getDate() - quizDate.getDate();
          
          if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
              yearsPassed--;
          }
          
          // Add the years passed to the original age
          loggedInAge = parseInt(age, 10) + yearsPassed;
        }
      } catch (error) {
        console.error('Failed to fetch first quiz data:', error);
        // Fallback to empty strings - will be handled by components
        loggedInGender = '';
        loggedInAge = '';
      }
    }
    
    
    
    
    let backboneId = '';
    
    if (availableQuestionnaires) {
     try {
       const availableQuestionnairesData = JSON.parse(availableQuestionnaires);
       // `id` is accepted because quiz-cta-handler's email fallback stores the raw
       // backbone record, which keys the result id as `id` rather than `backboneId`.
       const storedResultId =
         Number(availableQuestionnairesData?.backboneId) ||
         Number(availableQuestionnairesData?.quizId) ||
         Number(availableQuestionnairesData?.id);
       if (storedResultId) {
         backboneId = storedResultId;
         currentQuestionnaire = availableQuestionnairesData;
         // Repair the stored record so it resolves directly on the next load.
         if (Number(availableQuestionnairesData.backboneId) !== storedResultId) {
           availableQuestionnairesData.backboneId = storedResultId;
           try {
             localStorage.setItem('alyve.current_consultation', JSON.stringify(availableQuestionnairesData));
           } catch (err) {
             console.warn('Could not normalise stored consultation:', err);
           }
         }
       }
     } catch (e) {
       console.warn('Could not parse stored questionnaire JSON, clearing and fetching from API:', e);
       localStorage.removeItem('alyve.current_consultation');
     }
    }
    
    // If no backboneId, fetch the latest questionnaire from backend
    if (!backboneId) {
     try {
       const customerData = {
         id: window.customer_id,
         email: window.customer_email,
       };
       const latest = await getLatestQuestionnaire(customerData);
       if (latest && Array.isArray(latest) && latest.length > 0) {
         backboneId = Number(latest[0].id);
         currentQuestionnaire = latest[0];

         // Repair an existing consultation that had no usable id. Only when one is
         // already stored: a consultation without `questions` breaks the quiz app.
         if (availableQuestionnaires && backboneId) {
           try {
             const stored = JSON.parse(availableQuestionnaires);
             if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
               stored.backboneId = backboneId;
               localStorage.setItem('alyve.current_consultation', JSON.stringify(stored));
             }
           } catch (err) {
             console.warn('Could not persist resolved backboneId:', err);
           }
         }
       } else {
         
         window.location.href = '/pages/quiz-app';
         return;
       }
     } catch (error) {
       console.error('Failed to fetch latest questionnaire:', error);
        window.location.href = '/pages/quiz-app';
       return;
     }
    }
    
    
    
    // Now we have a backboneId, fetch populated questionnaire result
    finalBackboneId = backboneId;
    const payload = {
     result_id: backboneId,
     tokenId: customerData.id ,
    }; 
    
    async function fetchPopulatedWithRetry(url, payload, headers, maxRetries = 5, delayMs = 2000, onEachResponse = null) {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
  
          const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(payload),
          });
  
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          const data = await response.json();
  
          if (onEachResponse) onEachResponse(data);
  
          // Check if bio is populated (this is the critical data that takes time)
          if (data && data.bio !== null && data.bio !== undefined) {
            return data;
          }
          
          // // Bio not ready yet, wait and retry
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
          } else {
            // Last attempt failed, but return data anyway
            console.warn(`Bio still not available after ${maxRetries} attempts, proceeding anyway...`);
            return data;
          }
          
        } catch (error) {
          console.error(`❌ Attempt ${attempt + 1} failed:`, error);
          if (attempt === maxRetries - 1) throw error;
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
  
    // Track whether AI recommendations have been dispatched to avoid double-firing
    window.aiRecommendationsReceived = false;
    // Track whether bio has been applied, same reasoning - also lets
    // pollForRemainingData know it can stop polling once both are in.
    window.bioReceived = false;

    function hasAiRecommendations(data) {
      const recs = data?.recommendations;
      return Array.isArray(recs) && recs.length > 0 && recs.every(rec => rec.source === 'llm');
    }
  
    // Without formula.number (drives line item _#) and qualityCombinationNumber
    // (drives _qc) the cart can't send valid line item properties and the order
    // becomes corrupt at fulfilment — see orders #152987 / #153971.
    function isPopulatedRecordsValid(data) {
      return (
        data &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        data.formula &&
        data.formula.number != null &&
        data.qualityCombinationNumber != null
      );
    }
  
    function dispatchAiRecommendations(data) {
      if (window.aiRecommendationsReceived) return;
      window.aiRecommendationsReceived = true;
      const qualities = data.qualities || [];
      const recommendationPerQuality = qualities.map((quality) => {
        const recommendation = (data.recommendations || []).find((rec) =>
          rec.qualities.every((recq) => recq.name === quality.name)
        );
        return { quality, recommendation };
      });
      window.dispatchEvent(new CustomEvent('aiRecommendationsReady', {
        detail: { recommendationPerQuality },
      }));
    }
  
    function applyBio(freshData) {
      if (window.bioReceived || !(freshData && freshData.bio)) return;
      window.bioReceived = true;
      const profileBiohtml = document.getElementById('profile-text-container-section');
      if (profileBiohtml) profileBiohtml.textContent = freshData.bio;
      document.querySelector(".profile-section .profile-text")?.classList.remove("skeleton");
      // Defensive: only cache if the response still has formula/qc — never
      // poison the cache with a bio-only payload that would later trip our
      // validation gate.
      if (isPopulatedRecordsValid(freshData)) {
        localStorage.setItem("alyve_populated_quiz", JSON.stringify(freshData));
      } else {
        console.warn('[load-results] bio response missing formula/qc — not caching');
      }
    }

    // AUD-018: getuserBIO() used to run its own 3-attempt bio-focused loop and,
    // only once that finished, getAiRecommendations() started a completely
    // separate 10-attempt loop from scratch - even if bio had resolved on the
    // very first attempt - because it had no knowledge of bio at all. Up to 13
    // sequential round trips against the same expensive endpoint to resolve two
    // independent fields, and a bio that arrived mid-way through the second
    // loop was never picked up, since that loop only ever checked for recs.
    //
    // This checks both on every attempt and stops as soon as both are
    // satisfied, instead of exhausting one full budget before starting a
    // second one from zero.
    //
    // Attempts: 10, the larger of the two previous budgets - AI recommendations
    // were already given more time than bio, this must not shrink that.
    // Delay: 3000ms, recs' old cadence, not bio's old 5000ms - a deliberate
    // choice now that bio shares this loop, not a side effect of the merge.
    function pollForRemainingData(maxRetries = 10, delayMs = 3000) {
      if (!window.bioReceived) {
        document.querySelector(".profile-section .profile-text")?.classList.add("skeleton");
      }

      const headers = {
        'Content-Type': 'application/json',
        'x-customer-email': customerData.email,
        'x-customer-id': customerData.id,
        'x-customer-signature': customerData.signature,
        'x-shopify-shop-domain': 'alyve-store.myshopify.com',
      };

      (async () => {
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const response = await fetch(POPULATED_URL, {
              method: 'POST',
              headers,
              body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const data = await response.json();

            applyBio(data);
            if (hasAiRecommendations(data)) dispatchAiRecommendations(data);

            if (window.bioReceived && window.aiRecommendationsReceived) {
              return; // both satisfied, no need for further attempts
            }

            if (attempt < maxRetries - 1) {
              await new Promise(resolve => setTimeout(resolve, delayMs));
            }
          } catch (error) {
            console.error(`❌ Poll attempt ${attempt + 1} failed:`, error);
            if (attempt < maxRetries - 1) {
              await new Promise(resolve => setTimeout(resolve, delayMs));
            }
          }
        }

        // Retries exhausted - degrade gracefully rather than leaving either
        // side permanently stuck (bio: shimmer forever; recs: nothing logged).
        if (!window.bioReceived) {
          console.warn(`Bio still not available after ${maxRetries} attempts, proceeding anyway...`);
          document.querySelector(".profile-section .profile-text")?.classList.remove("skeleton");
        }
        if (!window.aiRecommendationsReceived) {
          console.warn('AI recs still not available after max retries.');
        }
      })();
    }

    // Shared by the cache-hit and cold-fetch paths below, so the decision
    // logic only has to change in one place.
    function startPollingIfNeeded(populatedRecords) {
      if (populatedRecords?.bio == null) {
        pollForRemainingData();
      } else {
        // applyBio() already no-ops if window.bioReceived is true or bio is
        // falsy, so calling it unconditionally here is safe - this is what
        // actually renders a bio that's already sitting in cached data,
        // instead of leaving the placeholder text and skeleton stuck forever.
        applyBio(populatedRecords);
        if (!window.aiRecommendationsReceived) {
          pollForRemainingData();
        }
      }
    }

    try {
      
      let populatedRecords = localStorage.getItem("alyve_populated_quiz") || null;
  
      // Parse + validate cached data up-front. A cache from an older response
      // shape (or from recovery returning a partial payload) that's missing
      // formula/qc is dropped here so we fall through to the fetch path and
      // either get a fresh response or redirect to the quiz.
      if (populatedRecords) {
        try {
          populatedRecords = JSON.parse(populatedRecords);
        } catch (e) {
          populatedRecords = null;
        }
        if (!isPopulatedRecordsValid(populatedRecords)) {
          console.warn('[load-results] Cached populatedRecords missing formula/qc — clearing cache');
          localStorage.removeItem('alyve_populated_quiz');
          populatedRecords = null;
        }
      }
  
      if (populatedRecords){
        // populatedRecords is already parsed and validated above
  
        // ✅ We have enough data to render the page
        dataLoaded = true;
        checkAndRemoveLoader();
  
  
        // 🧠 Bio and AI recommendations load INDEPENDENTLY of each other.
        
        // --- AI recommendations ---
        if (hasAiRecommendations(populatedRecords)) {
          dispatchAiRecommendations(populatedRecords); // already in localStorage → render now
        }
  
        // --- Bio (independent) ---
        
        startPollingIfNeeded(populatedRecords);
      }else{
  
        populatedRecords = await fetchPopulatedWithRetry(
          POPULATED_URL,
          payload,
          {
            'Content-Type': 'application/json',
            'x-customer-email': customerData.email,
            'x-customer-id': customerData.id,
            'x-customer-signature': customerData.signature,
            'x-shopify-shop-domain': 'alyve-store.myshopify.com',
          },
          3,
          2000,
          (data) => {
            if (hasAiRecommendations(data)) dispatchAiRecommendations(data);
          }
        );
        // Bio and AI recommendations load INDEPENDENTLY.
        // --- AI recommendations ---
        if (hasAiRecommendations(populatedRecords)) {
          dispatchAiRecommendations(populatedRecords); // already present → render now
        }
        // --- Bio (independent) ---
        startPollingIfNeeded(populatedRecords);
        // If the populated result is missing formula.number / qualityCombinationNumber,
        // the customer reached this page without a usable quiz (e.g. no health
        // goals → no quality combination match, or upstream API returned an
        // empty/partial payload masked as 200). Without those values the cart
        // can't send valid _# / _qc on the line item and fulfilment can't decide
        // what to ship — redirect to the quiz instead of rendering a broken
        // page with an enabled Add to Cart.
        if (populatedRecords && isPopulatedRecordsValid(populatedRecords)) {
          localStorage.setItem("alyve_populated_quiz", JSON.stringify(populatedRecords));
        } else {
          console.warn('[load-results] Populated result missing formula/qc — redirecting to /pages/quiz', {
            backboneId,
            populatedRecords,
          });
          localStorage.removeItem('alyve_populated_quiz');
          window.location.href = '/pages/quiz';
          return;
        }

      }

      // Not awaited: keeping the cart's copy current must never delay the page.
      syncCartQuizToken(populatedRecords).catch((e) =>
        console.warn('Could not sync the cart quiz token:', e)
      );




     const qualities = populatedRecords.qualities || [];
     const recommendations = populatedRecords.recommendations || [];
    
     // helper to find quality colour
     const getQualityColor = (qualityName) => {
       if (!Array.isArray(recommendations)) {
         return '#5EDFFF'; // fallback color
       }
       for (const recommendation of recommendations) {
         if (Array.isArray(recommendation.qualities)) {
           const matchingQuality = recommendation.qualities.find(
             (recQuality) =>
               recQuality.name.toLowerCase() === qualityName.toLowerCase()
           );
           if (matchingQuality && matchingQuality.colour) {
             return (window.alyveReadableColour||function(c){return c})(matchingQuality.colour);
           }
         }
       }
       return '#5EDFFF';
     };
    
     const recommendationPerQuality = qualities.map((quality) => {
       const recommendation = populatedRecords.recommendations.find((rec) =>
         rec.qualities.every((recq) => recq.name === quality.name)
       );
       return { quality, recommendation };
     });
    
    
    
       const quizResultData = {
         populatedRecords,
         recommendationPerQuality,
         customerData,
         allProducts,
         recommendedProducts,
         productsByCategory,
         blogs, 
         finalBackboneId, 
         currentQuestionnaire, 
         loggedInGender, 
         loggedInAge
    };
    
   
  window.quizResultData = quizResultData;
  
  // Make subscription flag globally accessible for product page
  window.hasPersonalisedVitaminsSubscription = customerData.hasPersonalisedVitaminsSubscription;
  
  //  Fire the event for any section that wants to react to it
  window.dispatchEvent(
  new CustomEvent("quizResultDataReady", {
   detail: quizResultData,
  })
  );
  
  
     // Mark data as loaded and check if we can remove loader
     dataLoaded = true;
     checkAndRemoveLoader();
  
  
       try {
      productsByCategory = await getProductsByCategory('personalised_vitamin');
    } catch (error) {
      console.error('Failed to fetch products by category:', error);
      productsByCategory = []; // Fallback to empty array
    }
      quizResultData.productsByCategory = productsByCategory
  
       window.dispatchEvent(
    new CustomEvent("quizResultDataReady", {
     detail: quizResultData,
    })
    );
  
  
    // // Fetch all products with individual error handling
    try {
      allProducts = await fetchProducts();
    } catch (error) {
      console.error('Failed to fetch all products:', error);
      allProducts = []; // Fallback to empty array
    }
  
      quizResultData.allProducts = allProducts
  
           window.dispatchEvent(
    new CustomEvent("quizResultDataReady", {
     detail: quizResultData,
    })
    );
  
    try {
      blogs = await getIngredientBlogs();
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      blogs = []; // Fallback to empty array
    }
  
    quizResultData.blogs = blogs
  
  
    // Fire a separate event for blogs so listeners can update without re-rendering the whole page
    window.dispatchEvent(
      new CustomEvent("blogsReady", {
        detail: { blogs },
      })
    );
    
     
     
    //  const listEl = document.querySelector('#tips-splide .splide__list');
    
    
    
     //if (!listEl) return;
    
     // Clear any existing content
     // listEl.innerHTML = '';
     // if (nutritionistTipsLoadingContainer) nutritionistTipsLoadingContainer.innerHTML = '';
    
     // if (recommendationPerQuality.length === 0) {
     //   // nothing to show, stop here
     //   return;
     // }
    
    
    
     // recommendationPerQuality.forEach((item) => {
     //   const qualityColor = getQualityColor(item.quality.name);
     //   const backgroundColor = qualityColor + '1A';
     //   const li = document.createElement('li');
     //   li.className = 'splide__slide';
     //   li.innerHTML = `
     //     <div class="tipBoxBlock">
     //       <div
     //         style="background-color: ${backgroundColor};
     //                border: 1px solid ${qualityColor};
     //                color: ${qualityColor};"
     //         class="tipsType">
     //         <p>${item.quality.name}</p>
     //       </div>
     //       <p class="tipsDescription">
     //         ${item.recommendation?.text || 'No recommendation available.'}
     //       </p>
     //     </div>
     //   `;
     //   listEl.appendChild(li);
     // });
    
     // // (Re)initialise Splide after slides injected
     // if (window.myTipsSplide) {
     //   window.myTipsSplide.destroy(true);
     // }
     // window.myTipsSplide = new Splide('#tips-splide', {
     //   type: 'slide',
     //   perPage: 3,
     //   gap: '40px',
     //   arrows: false,
     //   breakpoints: {
     //     1400:{ gap:'25px' },
     //     1200:{ perPage: 2 },
     //     800: { perPage: 2 },
     //     575: { perPage: 1  },
     //   },
     // }).mount();
    
  } catch (error) {
    console.error('❌ Failed to load populated questionnaire results:', error.message);
    
    // Still remove the loader even on error
    dataLoaded = true;
    checkAndRemoveLoader();
    if (window.alyveShowFormulaNotice) window.alyveShowFormulaNotice('error');

    // Show error message to user - this is critical data failure
    const loader = document.getElementById("page-loader");
    if (loader) {
      const hasQuizData = !!currentQuestionnaire;
      const hasProducts = productsByCategory && productsByCategory.length > 0;
      
      loader.innerHTML = `
        <div style="max-width: 600px; margin: 0 auto; text-align: center; padding: 40px 20px;">
          <h2 style="color: #dc2626; margin-bottom: 16px; font-family: 'Rebond', sans-serif;">Unable to Load Your Results</h2>
          <p style="color: #4b5563; margin-bottom: 24px; font-family: 'Roboto', sans-serif; line-height: 1.6;">
            We're having trouble loading your personalized quiz results. This could be due to a temporary network issue.
          </p>
          ${hasQuizData ? '<p style="color: #059669; margin-bottom: 16px;">✓ Quiz data found</p>' : ''}
          ${hasProducts ? '<p style="color: #059669; margin-bottom: 16px;">✓ Product data loaded</p>' : ''}
          <p style="color: #9ca3af; margin-top: 24px; font-size: 14px; font-family: 'Roboto', sans-serif;">
            If the problem persists, please contact our support team.
          </p>
        </div>
      `;
    }
  }
  });